import React from 'react';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import EditIcon from '@mui/icons-material/Edit';
import StraightenIcon from '@mui/icons-material/Straighten';
import { Divider } from '@mui/material';

import { notNullish } from '@paragon/utilities';

import { SelectedItemIdentifier } from '../../../../library/selection/types';
import { TransformationMenuItems } from '../../../../library/transformations/controls/TransformationMenuItems';
import { useAppMonitor } from '../../../../monitor';
import { scrollTo } from '../../../../scrolling';
import { beginCuttingRoofPlane, launchFasciaHeightsTool } from '../../../../store/actions/layout/roofPlaneActions';
import { handleStandardSceneItemsEditPropertiesAction } from '../../../../store/actions/sceneInteractionActions';
import { getAllRoofPlanesDictionary } from '../../../../store/selectors/layoutDataSelectors';
import { isPickingActive } from '../../../../store/selectors/pickingSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { AutoCutPlanesMenuItemButton } from '../../../interface/subpanels/properties/roof-planes/AutoCutPlanesButtons';
import { SceneMenuContent } from '../common/SceneMenuContent';
import { SceneMenuItem } from '../common/SceneMenuItem';
import { DeleteMenuItem } from '../shared-menu-items/DeleteMenuItem';

export function RoofPlaneMenu(props: { items: SelectedItemIdentifier[] }) {
  const monitor = useAppMonitor();
  const dispatch = useAppDispatch();

  const isPicking = useAppSelector(isPickingActive);

  const allRoofPlanes = useAppSelector(getAllRoofPlanesDictionary);
  const roofPlanes = props.items.map((item) => allRoofPlanes[item.guid]).filter(notNullish);

  if (roofPlanes.length < props.items.length) throw new Error('Unable to find roof plane for active menu.');

  return (
    <SceneMenuContent>
      <SceneMenuItem
        text="Edit Properties"
        icon={<EditIcon />}
        onClick={async () => {
          await dispatch(handleStandardSceneItemsEditPropertiesAction('roofPlane', roofPlanes));
          monitor.recordInteraction('Roof Plane => Edit Properties menu item clicked');
        }}
      />

      <TransformationMenuItems />

      {roofPlanes.length === 1 && (
        <SceneMenuItem
          text="Adjust Fascia Height"
          icon={<StraightenIcon />}
          onClick={() => {
            dispatch(launchFasciaHeightsTool());
            monitor.recordInteraction('Roof Plane => Adjust Fascia Height menu item clicked');
          }}
        />
      )}

      {roofPlanes.length === 1 && (
        <SceneMenuItem
          text={roofPlanes[0].cuts.length > 0 ? 'Re-Cut Plane' : 'Cut Plane'}
          icon={<ContentCutIcon />}
          disabled={isPicking}
          onClick={async () => {
            scrollTo('cutPlaneButton');
            await dispatch(beginCuttingRoofPlane(roofPlanes[0].guid));

            monitor.recordInteraction('Roof Plane => Begin Cutting menu item clicked');
          }}
        />
      )}

      <AutoCutPlanesMenuItemButton
        roofPlaneGuids={roofPlanes.map((plane) => plane.guid)}
        disabled={isPicking}
        monitorSource="Roof Plane"
      />

      <Divider />

      <DeleteMenuItem
        type="roofPlane"
        item={props.items}
        userVisibleTerm={roofPlanes.length === 1 ? 'Plane' : 'Planes'}
        onBeforeDelete={(proceed) => {
          const confirmationMessage = `Are you sure you want to delete ${roofPlanes.length === 1 ? 'this roof plane' : 'these roof planes'}?`;
          if (confirm(confirmationMessage)) {
            proceed();
          }
        }}
      />
    </SceneMenuContent>
  );
}
